import { useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { decryptField as decrypt } from '../lib/secureFields'
import { useCurrency } from '../hooks/useCurrency'
import { useLanguage } from '../hooks/useLanguage'
import FacturaScanner from '../components/FacturaScanner'
import SuggestionBox from '../components/SuggestionBox'
import TutorialMenu from '../components/TutorialMenu'
import { usePageTour } from '../hooks/usePageTour'
import { getHomeSteps } from '../tours/homeTour'
import { SPECIAL_THEMES } from '../lib/themesConfig'

// ─── // ─── NavButton ───────────────────────────────────────────────────────────────
function NavButton({ item }) {
  const { t } = useLanguage()
  const [pressed, setPressed] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)


  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

      {/* Burbuja "En construcción" */}
{item.disabled && showTooltip && (
  <div
    style={{
      position: 'absolute',
      bottom: 'calc(100% + 10px)',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#0f172a',
      color: '#fff',
      padding: '10px 14px',
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 500,
      boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
      border: '1px solid rgba(255,255,255,0.08)',
      zIndex: 99999,
      pointerEvents: 'none',

      /* IMPORTANTE */
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      width: 'max-content',
      minWidth: 'max-content',
    }}
  >
    <span>🚧</span>
    <span>{t('proximamente')}</span>

    <div
      style={{
        position: 'absolute',
        bottom: -5,
        left: '50%',
        width: 10,
        height: 10,
        background: '#0f172a',
        borderRight: '1px solid rgba(255,255,255,0.08)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        transform: 'translateX(-50%) rotate(45deg)',
      }}
    />
  </div>
)}

      <button
        onClick={item.disabled ? undefined : item.action}
        onMouseEnter={() => { setPressed(true); if (item.disabled) setShowTooltip(true) }}
        onMouseLeave={() => { setPressed(false); setShowTooltip(false) }}
        onTouchStart={() => { setPressed(true); if (item.disabled) setShowTooltip(true) }}
        onTouchEnd={() => {
          setTimeout(() => { setPressed(false); setShowTooltip(false) }, 1500)
        }}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          minWidth: 60,
          padding: '6px 10px',
          position: 'relative',
          color: item.disabled ? 'var(--color-muted)' : 'var(--color-text)',
          opacity: item.disabled ? 0.45 : 1,
        }}
      >
        {/* Fondo tipo WhatsApp */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: pressed && !item.disabled ? 54 : 0,
          height: 36,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
          pointerEvents: 'none',
        }} />

        {/* Ícono */}
        <div style={{
          fontSize: 22,
          lineHeight: 1,
          transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
          transform: pressed && !item.disabled ? 'scale(1.18) translateY(-1px)' : 'scale(1)',
          position: 'relative',
          zIndex: 1,
        }}>
          {item.icon}
        </div>

        {/* Texto */}
        <div style={{
          fontSize: 11,
          fontWeight: pressed && !item.disabled ? 700 : 600,
          opacity: pressed && !item.disabled ? 1 : 0.75,
          color: pressed && !item.disabled ? 'var(--color-primary, #22c55e)' : 'inherit',
          transition: 'all 0.18s ease',
          position: 'relative',
          zIndex: 1,
        }}>
          {item.label}
        </div>
      </button>
    </div>
  )
}
const ADMIN_USER_ID = '18af217b-614a-4f65-a471-518d82e70a1d' // el mismo UUID que usaste en las policies
// ─── Home ─────────────────────────────────────────────────────────────────────
export default function Home() {
    const LOCALE_POR_IDIOMA = {
  es: 'es-CO',
  en: 'en-US',
  fr: 'fr-FR'
}
  const { t, language } = useLanguage()
  const locale = LOCALE_POR_IDIOMA[language] || 'es-CO'
  const navigate = useNavigate()
  //suscripcion
const [subscription, setSubscription] = useState(null)
const isPremium = subscription?.status === 'active' && new Date(subscription.expires_at) > new Date()
const [user, setUser] = useState(null)
const isAdmin = user?.id === ADMIN_USER_ID
const [loading, setLoading] = useState(true)
const [totalAhorros, setTotalAhorros] = useState(0)
const [alertasPendientes, setAlertasPendientes] = useState(0)
// REEMPLAZA const [featuredSaving, setFeaturedSaving] = useState(null) POR ESTAS TRES:
const [savingsList, setSavingsList] = useState([])
const [featuredIndex, setFeaturedIndex] = useState(0)
const featuredSaving = savingsList[featuredIndex] || null
// ─────────────────────────────────────────────────────
const [showNotifications, setShowNotifications] = useState(false)
const [notifications, setNotifications] = useState([])
const [allMovimientos, setAllMovimientos] = useState([])
const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
const [showGeneral, setShowGeneral] = useState(() => {
  const saved = localStorage.getItem('app-vista-general')
  return saved === 'true'
})
const [showExtras, setShowExtras] = useState(false)
const notiRef = useRef(null)
const { format } = useCurrency()

usePageTour(
  (t) => getHomeSteps({
    t,
    setUi: (ui) => {
      setShowExtras(ui === 'extras' || ui === 'notif')
      setShowNotifications(ui === 'notif')
    }
  }),
  () => { setShowExtras(false); setShowNotifications(false) }
)


  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  // ── Filtrado de movimientos ──────────────────────────────────────────────
  const movimientosFiltrados = useMemo(() => {
    if (showGeneral) return allMovimientos
    return allMovimientos.filter(m => {
      const fecha = new Date(m.date + 'T00:00:00')
      return fecha.getMonth() === selectedMonth && fecha.getFullYear() === selectedYear
    })
  }, [allMovimientos, showGeneral, selectedMonth, selectedYear])

  const totalIngresos = useMemo(() =>
    movimientosFiltrados.filter(m => m.type === 'income').reduce((acc, i) => acc + Number(i.amount), 0),
    [movimientosFiltrados]
  )
  const totalGastos = useMemo(() =>
    movimientosFiltrados.filter(m => m.type === 'expense').reduce((acc, i) => acc + Number(i.amount), 0),
    [movimientosFiltrados]
  )
  const balance = totalIngresos - totalGastos

  const healthScore =
  totalIngresos <= 0
    ? 0
    : Math.min(
        100,
        Math.max(
          0,
          Math.round((balance / totalIngresos) * 100)
        )
      )

  const [randomSavingMessage] = useState(() => {
    const msgs = [
      t('saving_msg_1'), t('saving_msg_2'), t('saving_msg_3'),
      t('saving_msg_4'), t('saving_msg_5'), t('saving_msg_6'), t('saving_msg_7'),
    ]
    return msgs[Math.floor(Math.random() * msgs.length)]
  })

  const [randomMessage] = useState(() => {
    const msgs = [
      t('bonzai_msg_1'), t('bonzai_msg_2'), t('bonzai_msg_3'),
      t('bonzai_msg_4'), t('bonzai_msg_5'), t('bonzai_msg_6'),
    ]
    return msgs[Math.floor(Math.random() * msgs.length)]
  })

  // ── Bancos de mensajes para Ingresos y Gastos (por rango) ───────────────
const incomeMsgBank = {
  zero: [t('income_msg_zero_1'), t('income_msg_zero_2'), t('income_msg_zero_3')],
  low:  [t('income_msg_low_1'), t('income_msg_low_2'), t('income_msg_low_3')],
  mid:  [t('income_msg_mid_1'), t('income_msg_mid_2'), t('income_msg_mid_3')],
  high: [t('income_msg_high_1'), t('income_msg_high_2'), t('income_msg_high_3')],
}

const expenseMsgBank = {
  zero: [t('expense_msg_zero_1'), t('expense_msg_zero_2'), t('expense_msg_zero_3')],
  low:  [t('expense_msg_low_1'), t('expense_msg_low_2'), t('expense_msg_low_3')],
  mid:  [t('expense_msg_mid_1'), t('expense_msg_mid_2'), t('expense_msg_mid_3')],
  high: [t('expense_msg_high_1'), t('expense_msg_high_2'), t('expense_msg_high_3')],
}

function getBracket(value, low, mid) {
  if (value === 0) return 'zero'
  if (value < low) return 'low'
  if (value < mid) return 'mid'
  return 'high'
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

const incomeBracket = getBracket(totalIngresos, 500000, 2000000)
const expenseBracket = getBracket(totalGastos, 300000, 1500000)

const [incomeMessage, setIncomeMessage] = useState(() => pickRandom(incomeMsgBank.zero))
const [expenseMessage, setExpenseMessage] = useState(() => pickRandom(expenseMsgBank.zero))

const lastIncomeBracket = useRef(incomeBracket)
const lastExpenseBracket = useRef(expenseBracket)

useEffect(() => {
  if (lastIncomeBracket.current !== incomeBracket) {
    lastIncomeBracket.current = incomeBracket
    setIncomeMessage(pickRandom(incomeMsgBank[incomeBracket]))
  }
}, [incomeBracket])

useEffect(() => {
  if (lastExpenseBracket.current !== expenseBracket) {
    lastExpenseBracket.current = expenseBracket
    setExpenseMessage(pickRandom(expenseMsgBank[expenseBracket]))
  }
}, [expenseBracket])

// ── Cerrar notificaciones al click afuera ────────────────────────────────
  useEffect(() => {
    function handleClickOutside(e) {
      if (notiRef.current && !notiRef.current.contains(e.target)) {
        setShowNotifications(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ── Intervalo rotación ahorros ───────────────────────────────────────────
  useEffect(() => {
    if (savingsList.length <= 1) return
    const interval = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % savingsList.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [savingsList])

const [currentTheme, setCurrentTheme] = useState(
  () => document.documentElement.getAttribute('data-theme')
)
const themeCfg = SPECIAL_THEMES[currentTheme] || null

useEffect(() => {
  const observer = new MutationObserver(() =>
    setCurrentTheme(document.documentElement.getAttribute('data-theme'))
  )
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  })
  return () => observer.disconnect()
}, [])

useEffect(() => {
  function handleVistaChange(e) {
    setShowGeneral(e.detail)
  }
  window.addEventListener('vistaChange', handleVistaChange)
  return () => window.removeEventListener('vistaChange', handleVistaChange)
}, [])

  // ── Carga de datos ───────────────────────────────────────────────────────
  useEffect(() => {
    let channel
    async function loadData() {
    try {
      setLoading(true)
      const { data, error: userError } = await supabase.auth.getUser()
      if (userError || !data?.user) { navigate('/login'); return }
      const currentUser = data.user
      setUser(currentUser)

      // ── Todas las queries principales en paralelo ──
      const [
        { data: movimientos },
        { data: alertas },
        { data: savingsData },
        { data: sharedData },
        { data: notiData },
        { data: subData },
      ] = await Promise.all([
        supabase.from('transactions').select('amount, type, date').eq('user_id', currentUser.id),
        supabase.from('alerts').select('id').eq('user_id', currentUser.id),
        supabase.from('savings').select('*').eq('user_id', currentUser.id),
        supabase.from('shared_savings').select('*')
          .or(`creator_id.eq.${currentUser.id},partner_id.eq.${currentUser.id}`)
          .eq('status', 'accepted'),
        supabase.from('notifications').select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase.from('subscriptions').select('status, expires_at')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])

      setAllMovimientos(movimientos || [])
      setAlertasPendientes(alertas?.length || 0)
      setNotifications(notiData || [])
      setSubscription(subData)

      // ── Transacciones de ahorros en 2 llamadas paralelas ──
      const savingIds = (savingsData || []).map(s => s.id)
      const sharedIds = (sharedData || []).map(s => s.id)

      const [
        { data: allSavingTxs },
        { data: allSharedTxs },
      ] = await Promise.all([
        savingIds.length > 0
          ? supabase.from('saving_transactions').select('amount, saving_id').in('saving_id', savingIds)
          : Promise.resolve({ data: [] }),
        sharedIds.length > 0
          ? supabase.from('shared_saving_transactions').select('amount, type, shared_saving_id').in('shared_saving_id', sharedIds)
          : Promise.resolve({ data: [] }),
      ])

      // ── Armar ahorros personales ──
      const savingsWithAmounts = await Promise.all(
        (savingsData || []).map(async (saving) => {
          const txs = (allSavingTxs || []).filter(t => t.saving_id === saving.id)
          const currentSaved = txs.reduce((acc, t) => acc + Number(t.amount || 0), 0)
          return {
            ...saving,
            name: saving.name ? await decrypt(saving.name) : '',
            current_saved: currentSaved,
            shared: false
          }
        })
      )

      // ── Armar ahorros compartidos ──
      const sharedWithAmounts = await Promise.all(
        (sharedData || []).map(async (saving) => {
          const txs = (allSharedTxs || []).filter(t => t.shared_saving_id === saving.id)
          const currentSaved = txs
            .filter(t => t.type === 'deposit')
            .reduce((acc, t) => acc + Number(t.amount || 0), 0)
          return {
            ...saving,
            name: saving.name ? await decrypt(saving.name) : '',
            current_saved: currentSaved,
            shared: true
          }
        })
      )

      const allSavings = [...savingsWithAmounts, ...sharedWithAmounts]
      setSavingsList(allSavings)
      setTotalAhorros(allSavings.reduce((acc, item) => acc + Number(item.current_saved || 0), 0))

      // ── Realtime ──
      channel = supabase
    .channel('home-realtime-' + currentUser.id)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'notifications',
      filter: `user_id=eq.${currentUser.id}` // ✅ de vuelta
    }, (payload) => {
      console.log('🔔 Evento recibido:', payload.new) // ← agrega esto
       if (payload.new.user_id !== currentUser.id) return
      setNotifications(prev => [payload.new, ...prev])
    })
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'alerts',
      filter: `user_id=eq.${currentUser.id}` // ✅ de vuelta
    }, () => {
      setAlertasPendientes(prev => prev + 1)
    })
    .on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'alerts',
      filter: `user_id=eq.${currentUser.id}` // ✅ de vuelta
    }, () => {
      setAlertasPendientes(prev => Math.max(0, prev - 1))
    })
    .subscribe() // también puedes quitar el console.log ya que todo funciona
      
      // Leer vista preferida guardada
    const savedVista = localStorage.getItem('app-vista-general')
    if (savedVista !== null) {
      setShowGeneral(savedVista === 'true')
    }

    } catch (err) {
      console.error('Error cargando datos:', err)
    } finally {
      setLoading(false)
    }
  }

  loadData()

  return () => {
    if (channel) supabase.removeChannel(channel)
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [])

  // ── Helpers de notificaciones ────────────────────────────────────────────
  async function markAllRead() {
    if (!user) return
    await supabase.from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  async function deleteNotification(id) {
    await supabase.from('notifications').delete().eq('id', id)
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

    

  const unreadCount = notifications.filter(n => !n.read).length
  
    // ── Datos del usuario ────────────────────────────────────────────────────
  const appMode  = user?.user_metadata?.app_mode || 'personal'
  const name     = user?.user_metadata?.display_name || user?.user_metadata?.full_name?.split(' ')[0] || 'Usuario'
  const isNegocio = appMode === 'negocio'

  // ── Datos del ahorro destacado ───────────────────────────────────────────
  let totalGoal = 0, savedAmount = 0, remaining = 0, progress = 0
  if (featuredSaving) {
    totalGoal   = Number(featuredSaving.target_amount || 0)
    savedAmount = Number(featuredSaving.current_saved || 0)
    remaining   = totalGoal - savedAmount
    progress    = totalGoal > 0 ? Math.min((savedAmount / totalGoal) * 100, 100) : 0
  }

  // ── Loading screen ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: themeCfg ? themeCfg.bg : 'var(--color-bg)' }}>
        <div
          style={{
            width: 70, height: 70, borderRadius: 20,
            background: themeCfg ? themeCfg.logoBg : 'linear-gradient(135deg,#cfd363,#ffffff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, overflow: 'hidden',
            boxShadow: themeCfg ? `0 0 30px ${themeCfg.glow}` : 'none',
            animation: themeCfg ? 'trophyFloat 2.5s ease-in-out infinite' : 'pulse 1.5s infinite'
          }}
        >
          {themeCfg ? themeCfg.emoji : (
            <img src="/icon.png" alt="Calypso" style={{ width: '65%', height: '65%', objectFit: 'contain' }} />
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>
            <span className="app-title">Calypso</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--color-muted)' }}>
            {themeCfg ? t(themeCfg.keys.loading) : t('cargando_normal')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: themeCfg ? themeCfg.accent : '#cfd363', animation: `bounce 0.6s infinite ${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    )
  }



  function getAvatarUrl(path) {
    if (!path) return ''
    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
    return publicUrl
  }
  
  function notiIcon(type) {
    const icons = {
      shared_saving_request: '🤝',
      shared_saving_accepted: '✅',
      shared_saving_rejected: '❌',
      shared_saving_delete_request: '🗑️',
      friend_request: '🛰️',
      friend_request_accepted: '🎉',
      new_message: '💬',
      alert: '⚠️',
      message: '💬',
      info: '📢',
      buzz: '📳',
    }

    return icons[type] || '🔔'
  }

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1)  return t('tiempo_ahora')
    if (mins < 60) return t('tiempo_min').replace('{n}', mins)
    const hrs = Math.floor(mins / 60)
    if (hrs < 24)  return t('tiempo_hora').replace('{n}', hrs)
    return t('tiempo_dia').replace('{n}', Math.floor(hrs / 24))
  }

/* ICONOS
  const stats = [
    { label: 'Ingresos del mes', value: `${format(totalIngresos)}`, icon: '📈', color: '#dcfce7', textColor: '#15803d' },
    { label: 'Gastos del mes',   value: `${format(totalGastos)}`,   icon: '📉', color: '#fef2f2', textColor: '#ef4444' },
    { label: 'Balance',          value: `${format(balance)}`,        icon: '💼', color: '#eff6ff', textColor: '#1d4ed8' },
    { label: isNegocio ? 'Ventas hoy' : 'Ahorro / Deudas', value: `$${totalAhorros.toLocaleString('es-CO')}`, icon: isNegocio ? '🧾' : '🐷', color: '#fefce8', textColor: '#a16207' },
  ]


  const quickActions = isNegocio
    ? [
        { label: 'Registrar venta',  icon: '💵', color: 'var(--color-primary)' },
        { label: 'Registrar gasto',  icon: '🧾', color: '#ef4444' },
        { label: 'Ver calendario',   icon: '📅', color: '#6366f1' },
        { label: 'Proyecciones',     icon: '📊', color: '#f59e0b' },
      ]
    : [
        { label: 'Registrar ingreso',   icon: '💵', color: 'var(--color-primary)', route: '/ingresos'  },
        { label: 'Registrar gasto',     icon: '💸', color: '#ef4444',              route: '/gastos'    },
        { label: 'Ver calendario',      icon: '📅', color: '#6366f1',              route: '/calendario' },
        { label: 'Mi ahorro & Mi Deuda',icon: '🐷', color: '#f59e0b',              route: '/ahorro'    },
      ]
*/

  // ── Render ───────────────────────────────────────────────────────────────
        return (
          <div
          style={{
            minHeight: '100vh',
            background: 'var(--color-bg)'
          }}
        >

      {/* ── HEADER ── */}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, background: 'var(--color-primary)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, overflow: 'hidden' }}>
              {themeCfg ? themeCfg.emoji : (
                <img src="/icon.png" alt="Calypso" style={{ width: '68%', height: '68%', objectFit: 'contain' }} />
              )}
            </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, background: '--color-primary-h'}}>{themeCfg ? t(themeCfg.keys.name) : 'Calypso'}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {isAdmin && (
                <span
                  style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                    background: 'linear-gradient(135deg,#f7b500,#fcd34d)',
                    color: '#1a1400',
                    letterSpacing: 0.5,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    boxShadow: '0 0 10px rgba(247,181,0,0.45)',
                  }}
                >
                  👑 Owner
                </span>
              )}
              {isPremium && (
                <span
                  onClick={() => navigate('/suscripcion')}
                  style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                    background: 'linear-gradient(135deg,#16a34a,#cfd363)', color: 'white',
                    cursor: 'pointer', letterSpacing: 0.5,
                    display: 'inline-block',
                  }}
                >
                  ⭐ Suscrito
                </span>
              )}
            </div>
          </div>
        </div>
        <TutorialMenu />

{/* Pill de iconos */}
<div style={{ display: 'flex', alignItems: 'center', background: 'var(--color-surface)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 999, padding: '4px 6px', gap: 2, backdropFilter: 'blur(16px)' }}>

  {/* Flecha desplegable */}
    <button data-tour="home-extras-btn" onClick={() => setShowExtras(!showExtras)} title={t('mas_opciones')}
    style={{ width: 20, height: 34, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 300, color: 'var(--color-muted)', transition: '0.2s' }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{showExtras ? '›' : '‹'}</button>

  {showExtras && (
  <>

          {/* Campana + panel notificaciones */}
          <div style={{ position: 'relative' }} ref={notiRef}>
            <button data-tour="home-notif-btn" onClick={() => setShowNotifications(!showNotifications)} title={t('notificaciones')}
              style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, transition: '0.2s', position: 'relative' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              🔔
              {unreadCount > 0 && (
                <div style={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: 999, background: '#ef4444', border: '2px solid rgba(15,23,42,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'white', padding: '0 3px' }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </button>

            {showNotifications && (
              <div data-tour="home-notif-panel" style={{ position: 'fixed', top: 60, right: 12, width: 'min(340px, calc(100vw - 24px))', maxHeight: '60vh', overflowY: 'auto', background: 'rgb(from var(--color-surface) r g b / 1)', border: '1px solid var(--color-border)', borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', zIndex: 999, padding: 14, animation: 'fadeIn 0.2s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>🔔 {t('notificaciones')}</div>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} style={{ fontSize: 11, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      {t('marcar_todo_leido')}
                    </button>
                  )}
                </div>

                {notifications.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--color-muted)', textAlign: 'center', padding: '24px 10px' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                    {t('sin_notificaciones')}
                  </div>
                ) : notifications.map(n => (
                      <div key={n.id}
                        onClick={() => {
                          setShowNotifications(false)

                          if (n.type === 'friend_request') {
                            navigate('/amigos')
                          }

                          if (n.type === 'new_message') {
                            navigate('/mensajes')
                          }
                        }}
                        style={{ padding: '10px 12px', borderRadius: 14, background: n.read ? 'var(--color-bg)' : 'rgba(59,130,246,0.08)', marginBottom: 8, border: n.read ? '1px solid var(--color-border)' : '1px solid rgba(59,130,246,0.2)', transition: '0.2s', cursor: n.type === 'friend_request' ? 'pointer' : 'default' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ fontSize: 20, flexShrink: 0, width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(59,130,246,0.15)', flexShrink: 0 }}>
                      {n.type === 'friend_request' && n.metadata?.from_avatar
                        ? <img src={getAvatarUrl(n.metadata.from_avatar)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span>{notiIcon(n.type)}</span>
                      }
                    </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-muted)', lineHeight: 1.4 }}>{n.message}</div>
                        <div style={{ fontSize: 10, marginTop: 4, color: '#6b7280' }}>{timeAgo(n.created_at)}</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteNotification(n.id)
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: 14,
                          padding: '0 2px',
                          flexShrink: 0
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Solicitud ahorro compartido */}
                    {n.type === 'shared_saving_request' && !n.read && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button
                          onClick={async () => {
                            await supabase.from('shared_savings').update({ status: 'accepted' }).eq('id', n.metadata?.saving_id)
                            await supabase.from('notifications').insert({
                              user_id: n.metadata?.from_user_id,
                              type: 'shared_saving_accepted',
                              title: '¡Solicitud aceptada!',
                              message: `${name} aceptó unirse al ahorro compartido "${n.metadata?.saving_name}".`,
                              metadata: { saving_id: n.metadata?.saving_id }
                            })
                            await markAllRead()
                            deleteNotification(n.id)
                          }}
                          style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#22c55e', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
                        >{t('aceptar')}</button>
                        <button
                          onClick={async () => {
                            await supabase.from('shared_savings').update({ status: 'rejected' }).eq('id', n.metadata?.saving_id)
                            await supabase.from('notifications').insert({
                              user_id: n.metadata?.from_user_id,
                              type: 'shared_saving_rejected',
                              title: 'Solicitud rechazada',
                              message: `${name} rechazó la invitación al ahorro "${n.metadata?.saving_name}".`,
                              metadata: { saving_id: n.metadata?.saving_id }
                            })
                            deleteNotification(n.id)
                          }}
                          style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
                        >{t('rechazar')}</button>
                      </div>
                    )}

                    {/* Solicitud eliminación ahorro compartido */}
                    {n.type === 'shared_saving_delete_request' && !n.read && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button
                          onClick={async () => {
                            const { data: sharedInfo } = await supabase.from('shared_savings').select('*').eq('id', n.metadata?.saving_id).single()
                            const { data: movs } = await supabase.from('shared_saving_transactions').select('*').eq('shared_saving_id', n.metadata?.saving_id)
                            const isCompleted = sharedInfo?.target_amount > 0 &&
                              (movs || []).filter(m => m.type === 'deposit').reduce((acc, m) => acc + m.amount, 0) >= sharedInfo?.target_amount

                            if (!isCompleted) {
                              await supabase.rpc('devolver_ahorro_compartido', { saving_id: n.metadata?.saving_id })
                            }
                            await supabase.from('shared_saving_transactions').delete().eq('shared_saving_id', n.metadata?.saving_id)
                            await supabase.from('shared_savings').delete().eq('id', n.metadata?.saving_id)
                            await supabase.from('notifications').insert({
                              user_id: n.metadata?.from_user_id,
                              type: 'info',
                              title: '✅ Eliminación aceptada',
                              message: isCompleted
                                ? `"${n.metadata?.saving_name}" fue eliminado correctamente.`
                                : `"${n.metadata?.saving_name}" fue eliminado y el dinero fue devuelto a cada uno.`,
                              metadata: {}
                            })
                            deleteNotification(n.id)
                          }}
                          style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
                        >{t('aceptar_eliminar')}</button>
                        <button onClick={() => deleteNotification(n.id)}
                          style={{ flex: 1, padding: 8, borderRadius: 10, border: 'none', background: '#333', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
                        >{t('rechazar_simple')}</button>
                      </div>
                    )}  
                    {/* Zumbido — botón para responder */}
                    {n.type === 'buzz' && !n.read && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={async () => {
                            // Marca como leída y navega al ahorro
                            await supabase.from('notifications').update({ read: true }).eq('id', n.id)
                            setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))
                            setShowNotifications(false)
                            navigate('/ahorro')
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: 10,
                            border: 'none',
                            background: 'rgba(99,102,241,0.15)',
                            color: '#a5b4fc',
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                          }}
                        >
                          {t('ver_ahorro_responder')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                
              </div>             
            )}
          </div>

          {/* Alertas */}
            <button data-tour="home-alertas-btn" onClick={() => navigate('/alertas')} title={t('alertas')}
            style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, transition: '0.2s', animation: alertasPendientes > 0 ? 'alertShake 0.8s infinite' : 'none' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >⚠️</button>
           </>
          )}
          
          {/* Separador */}
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 2px' }} />

          {/* Avatar */}
            <button data-tour="home-perfil-btn" onClick={() => navigate('/perfil')}
            style={{ width: 34, height: 34, borderRadius: '50%', border: '1.5px solid var(--color-primary-lt)', background: 'var(--color-primary-lt)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#22c55e', fontSize: 13, cursor: 'pointer', overflow: 'hidden', padding: 0, transition: '0.2s' }}
            onMouseEnter={e => e.currentTarget.style.border = '1.5px solid var(--color-primary)'}
            onMouseLeave={e => e.currentTarget.style.border = '1.5px solid var(--color-primary-lt)'}
          >
            {user?.user_metadata?.avatar_url
              ? <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : name.charAt(0).toUpperCase()
            }
          </button>

          {/* Salir */}
          <button onClick={handleLogout} title={t('cerrar_sesion')}
            style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v10" />
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <main style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 120px' }}>

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            className="home-greeting"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 600,
            }}
          >
          {themeCfg
            ? `${t(themeCfg.keys.greeting)} ${name}`
            : `${t('saludo_normal')} ${name}!`}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--color-muted)', marginTop: 4 }}>
          {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

          {/* Burbuja Bonzai Bot — ahora aquí, a la par de los botones */}
          {alertasPendientes > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 20%, var(--color-surface)), color-mix(in srgb, var(--color-primary) 8%, var(--color-surface)))', padding: '12px 16px', borderRadius: '18px 18px 18px 4px', maxWidth: 320, fontSize: 14, color: 'var(--color-text)', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', border: '1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)', animation: 'fadeIn 0.4s ease' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>{t('bonzai_bot')}</div>
                <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--color-text)', fontWeight: 600 }}>{t('alertas_pendientes_pre')} {alertasPendientes} {t('alertas_pendientes_post')}</div>
                {randomMessage}
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6, textAlign: 'right' }}>{t('ahora_mismo')}</div>
              </div>
            </div>
          )}
        </div>

        <div data-tour="home-herramientas" style={{ flexShrink: 0, marginTop: 4, display: 'flex', flexDirection: 'row-reverse', gap: 8, alignItems: 'flex-start' }}>
          <FacturaScanner
            userId={user?.id}
            onGastoRegistrado={(gasto) => {
              setAllMovimientos(prev => [...prev, { type: 'expense', amount: gasto.amount, date: gasto.date }])
            }}
          />
          <SuggestionBox userId={user?.id} userName={name} />
          <button
            onClick={() => navigate('/muro-sugerencias')}
            title={t('muro_sugerencias')}
            style={{
              width: 44, height: 44, borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'var(--color-surface)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            }}
          >
            📋
          </button>
        </div>
      </div>

        {/* Filtro de período */}
        <section data-tour="home-periodo" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 20, padding: '14px 16px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{t('periodo_titulo')}</div>
          <div style={{ fontSize: 11, color: 'var(--color-muted)', marginBottom: 12 }}>{t('periodo_desc')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {!showGeneral && (
              <input
                type="month"
                value={`${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`}
                onChange={e => { const [y, m] = e.target.value.split('-'); setSelectedYear(parseInt(y)); setSelectedMonth(parseInt(m) - 1) }}
                style={{ flex: 1, minWidth: 120, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '9px 13px', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}
              />
            )}
            <button
              onClick={() => setShowGeneral(!showGeneral)}
              style={{
                background: showGeneral
                  ? 'radial-gradient(ellipse at 60% 30%, var(--color-primary-lt), transparent 80%), var(--color-surface)'
                  : 'var(--color-surface)',
                color: showGeneral ? 'var(--color-primary)' : 'var(--color-muted)',
                border: `1px solid ${showGeneral ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 12, padding: '9px 14px',
                cursor: 'pointer', fontWeight: 700, fontSize: 13,
                transition: 'var(--transition)',
                backdropFilter: 'blur(10px)',
                boxShadow: showGeneral ? 'var(--shadow-btn)' : 'none',
                whiteSpace: 'nowrap', flexShrink: 0
              }}
            >
              {showGeneral ? t('vista_general_btn') : t('vista_mensual_btn')}
            </button>
          </div>
        </section>

        {/* Stats */}{/* Stats */}
<section style={{ marginBottom: 28 }}>
  <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>{t('resumen')}</h3>
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

    {/* INGRESOS */}
    {(() => {
      const msg = incomeMessage
      return (
        <div
          data-tour="home-ingresos"
          onClick={() => navigate('/ingresos')}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
          style={{ background: 'linear-gradient(135deg,var(--stat-income-1),var(--stat-income-2))', borderRadius: 20, padding: 18, color: 'white', minHeight: 170, position: 'relative', overflow: 'hidden', border: '1px solid color-mix(in srgb, var(--color-primary) 50%, transparent)', boxShadow: 'var(--shadow-card)', cursor: 'pointer', transition: 'transform 0.18s ease, box-shadow 0.18s ease' }}>
          <div style={{
          position: 'absolute', top: 0, right: 0, width: 140, height: 140,
          background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.10) 0 2px, transparent 2px 14px)',
          WebkitMaskImage: 'radial-gradient(circle at 100% 0%, black 40%, transparent 75%)',
          maskImage: 'radial-gradient(circle at 100% 0%, black 40%, transparent 75%)',
          pointerEvents: 'none',
        }} />
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>{showGeneral ? t('ingresos_totales') : t('ingresos_del_mes')}</div>
          <div style={{ 
          fontSize: 'clamp(18px, 5vw, 30px)',  // ← se achica automático
          fontWeight: 500, 
          marginBottom: 10,
          wordBreak: 'break-word',             // ← corta si no cabe
          overflowWrap: 'break-word',          // ← soporte cross-browser
          lineHeight: 1.1,
        }}>
          {format(totalIngresos)}
        </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>{msg}</div>
          <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: 'color-mix(in srgb, var(--color-primary) 90%, white)' }}>{t('tap_registrar_ingreso')}</div>
        </div>
      )
    })()}

    {/* GASTOS */}
    {(() => {
      const msg = expenseMessage
      return (
        <div
          data-tour="home-gastos"
          onClick={() => navigate('/gastos')}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
          style={{ background: 'linear-gradient(135deg,var(--stat-expense-1),var(--stat-expense-2))', borderRadius: 20, padding: 18, color: 'white', minHeight: 170, position: 'relative', overflow: 'hidden', border: '1px solid color-mix(in srgb, var(--color-primary-h) 35%, transparent)', boxShadow: 'var(--shadow-card)', cursor: 'pointer', transition: 'transform 0.18s ease, box-shadow 0.18s ease' }}>
          <div style={{
          position: 'absolute', top: 0, right: 0, width: 140, height: 140,
          background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.09) 0 2px, transparent 2px 14px)',
          WebkitMaskImage: 'radial-gradient(circle at 100% 0%, black 40%, transparent 75%)',
          maskImage: 'radial-gradient(circle at 100% 0%, black 40%, transparent 75%)',
          pointerEvents: 'none',
        }} />
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>{showGeneral ? t('gastos_totales') : t('gastos_del_mes')}</div>
                    <div style={{ 
          fontSize: 'clamp(18px, 5vw, 30px)',  // ← se achica automático
          fontWeight: 500, 
          marginBottom: 10,
          wordBreak: 'break-word',             // ← corta si no cabe
          overflowWrap: 'break-word',          // ← soporte cross-browser
          lineHeight: 1.1,
        }}>{format(totalGastos)}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>{msg}</div>
          <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: 'color-mix(in srgb, var(--color-primary) 60%, white)' }}>{t('tap_registrar_gasto')}</div>
        </div>
      )
    })()}

    {/* BALANCE */}
    {(() => {
      const isPositive = balance > 0, isNegative = balance < 0
      return (
        <div
          data-tour="home-balance"
          onClick={() => navigate('/Analiticas')}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
          style={{ background:
            balance > 0
              ? 'linear-gradient(135deg,var(--stat-balance-1),var(--stat-balance-2))'
              : balance < 0
                ? 'linear-gradient(135deg,var(--stat-expense-1),var(--stat-expense-2))'
                : 'linear-gradient(135deg,#64748b,#94a3b8)', borderRadius: 20, padding: 18, color: 'white', minHeight: 170, position: 'relative', overflow: 'hidden', border: `1px solid color-mix(in srgb, var(--color-accent) 35%, transparent)`, boxShadow: 'var(--shadow-card)', cursor: 'pointer', transition: 'transform 0.18s ease, box-shadow 0.18s ease' }}>
          <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>{t('balance_general')}</div>
                    <div style={{ 
          fontSize: 'clamp(18px, 5vw, 30px)',  // ← se achica automático
          fontWeight: 500, 
          marginBottom: 10,
          wordBreak: 'break-word',             // ← corta si no cabe
          overflowWrap: 'break-word',          // ← soporte cross-browser
          lineHeight: 1.1,
        }}>{format(balance)}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>
            {isPositive && t('balance_positivo')}
            {isNegative && t('balance_negativo')}
            {!isPositive && !isNegative && t('balance_neutro')}
          </div>
          <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: 'color-mix(in srgb, var(--color-primary) 80%, white)' }}>
            {t('tap_ver_analisis')}
          </div>
        </div>
      )
    })()}

    {/* AHORRO / DEUDAS */}
    <div
      data-tour="home-ahorro"
      onClick={() => navigate('/ahorro')}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)' }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
      style={{ background:'linear-gradient(135deg,var(--stat-saving-1),var(--stat-saving-2))', borderRadius: 20, padding: 18, color: 'white', position: 'relative', overflow: 'hidden', minHeight: 170, border: '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)', boxShadow: 'var(--shadow-card)', cursor: 'pointer', transition: 'transform 0.18s ease, box-shadow 0.18s ease' }}>
      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>{t('elemento_destacado')}</div>
      {featuredSaving ? (
        <>
          <div style={{ fontSize: 22, fontWeight: 400, marginBottom: 6 }}>{featuredSaving.name}</div>
          <div style={{ 
            fontSize: 'clamp(16px, 4.5vw, 28px)',
            fontWeight: 800,
            color: 'color-mix(in srgb, var(--color-primary) 85%, white)',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.1,
            marginBottom: 4,
          }}>
            {format(savedAmount)}
          </div>
          <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
            {t('meta')} {format(totalGoal)}
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ width: '100%', height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'var(--color-primary)', borderRadius: 999, transition: '0.4s ease' }} />
            </div>
            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 11, opacity: 0.8 }}>
              <span>{progress.toFixed(0)}%</span>
              <span>{t('restante')} {format(remaining)}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, marginTop: 12, color: 'color-mix(in srgb, var(--color-primary) 75%, white)', fontWeight: 700 }}>
            {featuredSaving.type === 'debt' ? t('tap_ver_deudas') : t('tap_ver_ahorros')}
          </div>
          {featuredSaving.shared && (
            <div style={{ fontSize: 10, color: '#60a5fa', fontWeight: 700, marginTop: 2 }}>{t('compartido')}</div>
          )}
        </>
      ) : (
        <>
          <div style={{ marginTop: 18, fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>{randomSavingMessage}</div>
          <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: 'color-mix(in srgb, var(--color-primary) 80%, white)' }}>{t('tap_crear_ahorro')}</div>
        </>
      )}
    </div>
{/* DECLARACIÓN DE RENTA */}
    <div
      data-tour="home-renta"
      onClick={() => navigate('/declaracion-renta')}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.005)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)' }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
      style={{
        gridColumn: '1 / -1',
        background: 'linear-gradient(135deg,var(--stat-declaracion-1),var(--stat-declaracion-2))',
        borderRadius: 20,
        padding: 18,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid color-mix(in srgb, var(--color-primary) 35%, transparent)',
        boxShadow: 'var(--shadow-card)',
        cursor: 'pointer',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap'
      }}
    >
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 220, height: '100%',
        background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0 2px, transparent 2px 14px)',
        WebkitMaskImage: 'radial-gradient(circle at 100% 50%, black 35%, transparent 75%)',
        maskImage: 'radial-gradient(circle at 100% 50%, black 35%, transparent 75%)',
        pointerEvents: 'none',
      }} />
      <div>
        <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>🧾 {t('declaracion_renta_card_titulo')}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9, maxWidth: 420 }}>{t('declaracion_renta_card_desc')}</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'color-mix(in srgb, var(--color-primary) 85%, white)', whiteSpace: 'nowrap' }}>
        {t('declaracion_renta_card_cta')}
      </div>
    </div>

  </div>
</section>
      </main>

      {/* ── BOTTOM NAV ── */}
      <nav data-tour="home-nav" style={{ position: 'fixed', bottom: 14, left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 24px)', maxWidth: 520, background: 'var(--color-surface)', backdropFilter: 'blur(18px)', border: '1px solid var(--color-border)', borderRadius: 28, padding: '10px 14px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', zIndex: 9999, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
      {[
        { icon: '📅', label: t('nav_calendario'),  action: () => navigate('/Calendario') },
        { icon: '👥', label: t('nav_amigos'),    action: () => navigate('/amigos') },
        { icon: `💬${
          notifications.filter(
            n => n.type === 'new_message' && !n.read
          ).length > 0
            ? ` (${notifications.filter(
                n => n.type === 'new_message' && !n.read
              ).length})`
            : ''
        }`,
        label: t('nav_mensajes'),
        action: () => navigate('/mensajes')
      },
        { icon: '🤖', label: t('nav_ia'),       action: () => { navigate('/ia') }, /*disabled: true */},
      ].map((item) => (
        <NavButton key={item.label} item={item} />
      ))}
      </nav>
    </div>
  )
}